<template>
  <div>
    <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-4">
      Cierre de Caja Diario (Reporte Z)
    </h2>

    <div class="mb-4 text-gray-600 dark:text-gray-300">
      <strong>Fecha:</strong> {{ fechaHoy }}
    </div>

    <div v-if="ventas.length">
      <table class="w-full table-auto text-sm border mb-6">
        <thead class="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100">
          <tr>
            <th class="p-2 border">Hora</th>
            <th class="p-2 border">Método</th>
            <th class="p-2 border">Subtotal</th>
            <th class="p-2 border">Impuestos</th>
            <th class="p-2 border">Total</th>
            <th class="p-2 border">Origen</th>
          </tr>
        </thead>
        <tbody class="text-gray-700 dark:text-gray-200">
          <tr v-for="(v, i) in ventas" :key="i" class="hover:bg-gray-50 dark:hover:bg-gray-800">
            <td class="p-2 border">{{ v.fecha.toDate().toLocaleTimeString() }}</td>
            <td class="p-2 border capitalize">{{ v.metodoPago }}</td>
            <td class="p-2 border">${{ v.subtotal.toFixed(2) }}</td>
            <td class="p-2 border">${{ v.impuestos.toFixed(2) }}</td>
            <td class="p-2 border font-semibold text-green-600 dark:text-green-400">
              ${{ v.total.toFixed(2) }}
            </td>
            <td class="p-2 border">{{ v.origen }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Totales -->
      <div class="grid gap-2 text-sm text-gray-700 dark:text-gray-300">
        <div><strong>Total Ventas:</strong> ${{ total.toFixed(2) }}</div>
        <div><strong>Total Efectivo:</strong> ${{ totalEfectivo.toFixed(2) }}</div>
        <div><strong>Total Tarjeta:</strong> ${{ totalTarjeta.toFixed(2) }}</div>
        <div><strong>Total Impuestos:</strong> ${{ totalImpuestos.toFixed(2) }}</div>
        <div><strong>Cantidad de Ventas:</strong> {{ ventas.length }}</div>
        <div><strong>Productos:</strong> {{ cantidadProductos }}</div>
        <div><strong>Servicios:</strong> {{ cantidadServicios }}</div>
        <div><strong>Recomendaciones:</strong> {{ cantidadRecomendaciones }}</div>

      </div>
    </div>

    <!-- Si no hay ventas -->
    <div v-else class="text-gray-500 dark:text-gray-400 mt-6 text-center">
      No hay ventas registradas hoy.
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted } from "vue";
import { db } from "@/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  documentId,
} from "firebase/firestore";

// Fecha de hoy formateada
const fechaHoy = new Date().toLocaleDateString();

// Timestamp de hoy
const hoy = new Date();
const desdeTS = Timestamp.fromDate(new Date(hoy.setHours(0, 0, 0, 0)));
const hastaTS = Timestamp.fromDate(new Date(hoy.setHours(23, 59, 59, 999)));

// Refs para totales
const ventas = ref([]);
const total = ref(0);
const totalEfectivo = ref(0);
const totalTarjeta = ref(0);
const totalImpuestos = ref(0);
const cantidadProductos = ref(0);
const cantidadServicios = ref(0);
const cantidadRecomendaciones = ref(0);

const cargarVentasHoy = async () => {
  const snap = await getDocs(
    query(
      collection(db, "ventas"),
      where("fecha", ">=", desdeTS),
      where("fecha", "<=", hastaTS)
    )
  );

  const ventasDocs = [];
  const idsProductos = new Set();

  snap.forEach((doc) => {
    const data = doc.data();
    ventasDocs.push({ id: doc.id, ...data });

    if (Array.isArray(data.carrito)) {
      data.carrito.forEach((item) => {
        if (item.id) {
          idsProductos.add(item.id.trim());
        }
      });
    }
  });

  const productosMap = new Map();
  const idsArray = Array.from(idsProductos);
  console.log("📦 IDs únicos de productos/servicios:", idsArray);

  for (let i = 0; i < idsArray.length; i += 10) {
    const chunk = idsArray.slice(i, i + 10);
    const productosSnap = await getDocs(
      query(collection(db, "productos"), where(documentId(), "in", chunk))
    );
    productosSnap.forEach((doc) => {
      productosMap.set(doc.id, doc.data());
    });
  }

  console.log("✅ Productos cargados en productosMap:", Array.from(productosMap.keys()));

  ventas.value = [];
  total.value = totalEfectivo.value = totalTarjeta.value = totalImpuestos.value = 0;
  cantidadProductos.value = cantidadServicios.value = 0;

  for (const venta of ventasDocs) {
    const subtotal = Number(venta.subtotal) || 0;
    let impuestos = Number(venta.impuestos) || 0;
    if (impuestos > subtotal) impuestos = 0;

    const totalVenta = subtotal + impuestos;
    const metodo = venta.metodoPago || "desconocido";
    const origen = venta.origen || "POS";

    total.value += totalVenta;
    totalImpuestos.value += impuestos;

    if (metodo === "efectivo") {
      totalEfectivo.value += totalVenta;
    } else {
      totalTarjeta.value += totalVenta;
    }

    const items = [];

    // ✅ NUEVO: Usar 'items' si existen, sino 'carrito'
    const fuenteItems = Array.isArray(venta.items) && venta.items.length > 0
      ? venta.items
      : Array.isArray(venta.carrito)
        ? venta.carrito
        : [];

    for (const item of fuenteItems) {
      let tipo, nombre, cantidad, precio;

      if ('tipo' in item) {
        // ✅ viene de items[] directo
        tipo = String(item.tipo || "").toLowerCase();
        nombre = item.nombre || "Sin nombre";
        cantidad = item.cantidad || 1;
        precio = item.precio || 0;
      } else {
        // ✅ viene de carrito[], usamos productosMap
        const itemId = typeof item.id === "string" ? item.id.trim() : null;
        if (!itemId) {
          console.warn("❌ Item sin ID válido:", item);
          continue;
        }

        const producto = productosMap.get(itemId);
        if (!producto) {
          console.warn("❌ Producto no encontrado en productosMap para ID:", itemId);
          continue;
        }

        tipo = String(producto.tipo || "").toLowerCase();
        nombre = producto.nombre || "Desconocido";
        cantidad = item.cantidad || 1;
        precio = item.precio || 0;
      }

      console.log("🧾 Item procesado:", { tipo, nombre, cantidad, precio });

      if (tipo === "producto") {
  cantidadProductos.value += cantidad;
} else if (tipo === "servicio") {
  cantidadServicios.value += cantidad;
} else if (tipo === "recomendacion" || tipo === "recomendaciones") {
  cantidadRecomendaciones.value += cantidad;
} else {
  console.warn("⚠️ Tipo desconocido en item:", item);
}


      items.push({ nombre, tipo, cantidad, precio });
    }

    ventas.value.push({
      fecha: venta.fecha,
      metodoPago: metodo,
      subtotal,
      impuestos,
      total: totalVenta,
      origen,
      items,
    });
  }

  console.log("🧮 Total Productos:", cantidadProductos.value);
  console.log("🧮 Total Servicios:", cantidadServicios.value);


};


onMounted(cargarVentasHoy);
</script>
